import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getToken } from '../../../../features/token/tokenSaver';
import { getCampaignDetails } from '../../../../services/getCampaignDetails';
import '../AdvertiserDashboardNewCampaign/AdvertiserDashboardNewCampaign.css';
import '../AdvertiserDashboardNewCampaign/CreativeEditorModal.css';
import CreativeCropModal from '../AdvertiserDashboardNewCampaign/CreativeCropModal';
import CreativePreviewModal from '../AdvertiserDashboardNewCampaign/CreativePreviewModal';
import CreativeFileUpload from '../AdvertiserDashboardNewCampaign/CreativeFileUpload';
import CreativeProcessingOverlay from '../AdvertiserDashboardNewCampaign/CreativeProcessingOverlay';
import {
    blobToPreviewUrl,
    blobToRawBase64,
    ensureBlobUnderMaxBytes,
    fileToRawBase64,
    getCroppedImageBlob,
    isAllowedCreativeFile,
    isImageFile,
    isTargetAspectRatio,
    isVideoFile,
    loadVideoDimensions,
    MAX_CREATIVE_BYTES,
    MAX_CREATIVE_VIDEO_BYTES,
    MAX_CREATIVE_VIDEO_TRANSCODE_THRESHOLD_BYTES,
    creativeVideoHint,
    OUTPUT_SIZE,
    prepareImageFileForUpload,
    resizeImageToOutputBlob,
} from '../../../../utils/creativeAspectRatio';

const CREATIVE_INPUT_IDS = {
    horizontal: 'fileUploadCompletePrepaidHorizontal',
    vertical: 'fileUploadCompletePrepaidVertical',
};

export default function AdvertiserCompletePrepaidCampaign() {
    const { state } = useLocation();
    const navigate = useNavigate();
    const token = useSelector(getToken);
    const adId = state?.adId;

    const [campaignName, setCampaignName] = useState('');
    const [campaignHorizontalContent, setCampaignHorizontalContent] = useState('');
    const [campaignVerticalContent, setCampaignVerticalContent] = useState('');
    const [creativeFileNames, setCreativeFileNames] = useState({ horizontal: '', vertical: '' });
    const [creativeEditor, setCreativeEditor] = useState(null);
    const [cropExporting, setCropExporting] = useState(false);
    const [creativeProcessing, setCreativeProcessing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [reservationLabel, setReservationLabel] = useState('');

    useEffect(() => {
        if (!adId) {
            navigate('/advertiserDashboard/campaigns', { replace: true });
            return;
        }

        getCampaignDetails(token, adId)
            .then((response) => {
                const ad = response.data?.advertismentData || {};
                if (ad.SubscriptionStatus !== 'prepaid_reserved') {
                    setLoadError('This campaign is not awaiting creative upload.');
                    return;
                }
                setReservationLabel(ad.AdvertisementName || 'Prepaid reservation');
                setCampaignName('');
            })
            .catch(() => setLoadError('Unable to load reservation details.'))
            .finally(() => setLoading(false));
    }, [adId, token, navigate]);

    useEffect(() => {
        return () => {
            if (creativeEditor?.previewUrl?.startsWith('blob:')) {
                URL.revokeObjectURL(creativeEditor.previewUrl);
            }
        };
    }, [creativeEditor]);

    const revokeCreativeEditorUrls = useCallback((editor) => {
        if (!editor) return;
        if (editor.previewUrl?.startsWith('blob:')) {
            URL.revokeObjectURL(editor.previewUrl);
        }
    }, []);

    const clearCreativeInput = useCallback((inputId) => {
        const input = document.querySelector(`#${inputId}`);
        if (input) {
            input.value = '';
        }
    }, []);

    const closeCreativeEditor = useCallback(() => {
        setCreativeEditor((prev) => {
            revokeCreativeEditorUrls(prev);
            return null;
        });
        setCropExporting(false);
    }, [revokeCreativeEditorUrls]);

    const clearSlotContent = useCallback((slot) => {
        if (slot === 'horizontal') {
            setCampaignHorizontalContent('');
        } else {
            setCampaignVerticalContent('');
        }
        setCreativeFileNames((prev) => ({ ...prev, [slot]: '' }));
    }, []);

    const commitCreativeBase64 = useCallback((slot, base64, fileName) => {
        if (slot === 'horizontal') {
            setCampaignHorizontalContent(base64);
        } else {
            setCampaignVerticalContent(base64);
        }
        setCreativeFileNames((prev) => ({ ...prev, [slot]: fileName }));
    }, []);

    const openImagePreviewEditor = useCallback(async (slot, file, dataUrl, inputId) => {
        const outputSize = OUTPUT_SIZE[slot];
        let blob = await resizeImageToOutputBlob(dataUrl, outputSize);
        blob = await ensureBlobUnderMaxBytes(blob);
        const previewUrl = blobToPreviewUrl(blob);
        setCreativeEditor({
            slot,
            step: 'preview',
            imageSrc: dataUrl,
            file,
            previewUrl,
            pendingBlob: blob,
            isVideo: false,
            inputId,
        });
    }, []);

    const processCreativeUpload = useCallback(async (e, slot) => {
        const inputId = CREATIVE_INPUT_IDS[slot];
        const file = e.target.files?.[0];
        if (!file) {
            return;
        }

        if (!isAllowedCreativeFile(file)) {
            alert('Please upload a file with the file type JPG, PNG, MP4, MOV, or WebM');
            clearCreativeInput(inputId);
            setCreativeFileNames((prev) => ({ ...prev, [slot]: '' }));
            return;
        }

        closeCreativeEditor();
        clearSlotContent(slot);
        setCreativeFileNames((prev) => ({ ...prev, [slot]: file.name }));

        const needsResize = isImageFile(file) && file.size > MAX_CREATIVE_BYTES;
        const processingMessage = isVideoFile(file)
            ? 'Processing your video…'
            : needsResize
                ? 'Resizing your image…'
                : 'Preparing your image…';

        setCreativeProcessing({ message: processingMessage });

        try {
            if (isVideoFile(file)) {
                if (file.size > MAX_CREATIVE_VIDEO_BYTES) {
                    alert('This video is too large. Maximum upload size is 80MB. Try a shorter clip or lower resolution.');
                    clearCreativeInput(inputId);
                    setCreativeFileNames((prev) => ({ ...prev, [slot]: '' }));
                    return;
                }
                if (file.size > MAX_CREATIVE_VIDEO_TRANSCODE_THRESHOLD_BYTES) {
                    alert('Large videos are automatically re-encoded to reduce size and fit signage playback.');
                }
                const { width, height } = await loadVideoDimensions(file);
                if (!isTargetAspectRatio(width, height, slot)) {
                    alert(slot === 'horizontal'
                        ? 'Video aspect ratio must be 16:9'
                        : 'Video aspect ratio must be 9:16');
                    clearCreativeInput(inputId);
                    setCreativeFileNames((prev) => ({ ...prev, [slot]: '' }));
                    return;
                }
                const base64 = await fileToRawBase64(file);
                commitCreativeBase64(slot, base64, file.name);
                return;
            }

            if (!isImageFile(file)) {
                alert('Please upload a file with the file type JPG, PNG, MP4, MOV, or WebM');
                clearCreativeInput(inputId);
                setCreativeFileNames((prev) => ({ ...prev, [slot]: '' }));
                return;
            }

            const { file: imageFile, dataUrl } = await prepareImageFileForUpload(file);
            const image = await new Promise((resolve, reject) => {
                const img = new Image();
                img.onload = () => resolve(img);
                img.onerror = reject;
                img.src = dataUrl;
            });

            if (isTargetAspectRatio(image.width, image.height, slot)) {
                await openImagePreviewEditor(slot, imageFile, dataUrl, inputId);
                return;
            }

            setCreativeEditor({
                slot,
                step: 'crop',
                imageSrc: dataUrl,
                file: imageFile,
                inputId,
            });
        } catch (err) {
            console.error('Creative upload failed:', err);
            alert('Could not process this file. Please try another image or video.');
            clearCreativeInput(inputId);
            setCreativeFileNames((prev) => ({ ...prev, [slot]: '' }));
        } finally {
            setCreativeProcessing(null);
        }
    }, [
        clearCreativeInput,
        clearSlotContent,
        closeCreativeEditor,
        commitCreativeBase64,
        openImagePreviewEditor,
    ]);

    const handleCreativeEditorCancel = useCallback(() => {
        if (creativeEditor?.inputId) {
            clearCreativeInput(creativeEditor.inputId);
        }
        if (creativeEditor?.slot) {
            setCreativeFileNames((prev) => ({ ...prev, [creativeEditor.slot]: '' }));
        }
        closeCreativeEditor();
    }, [creativeEditor, clearCreativeInput, closeCreativeEditor]);

    const handleCropNext = useCallback(async ({ croppedAreaPixels, rotation }) => {
        if (!creativeEditor || creativeEditor.step !== 'crop') {
            return;
        }
        setCropExporting(true);
        try {
            let blob = await getCroppedImageBlob(
                creativeEditor.imageSrc,
                croppedAreaPixels,
                rotation,
                OUTPUT_SIZE[creativeEditor.slot]
            );
            blob = await ensureBlobUnderMaxBytes(blob);
            const previewUrl = blobToPreviewUrl(blob);
            setCreativeEditor((prev) => {
                revokeCreativeEditorUrls(prev);
                return {
                    slot: prev.slot,
                    step: 'preview',
                    imageSrc: prev.imageSrc,
                    file: prev.file,
                    previewUrl,
                    pendingBlob: blob,
                    isVideo: false,
                    inputId: prev.inputId,
                };
            });
        } catch (err) {
            console.error('Crop export failed:', err);
            alert('Could not crop this image. Please try again.');
        } finally {
            setCropExporting(false);
        }
    }, [creativeEditor, revokeCreativeEditorUrls]);

    const handlePreviewNext = useCallback(async () => {
        if (!creativeEditor?.pendingBlob) {
            return;
        }
        try {
            const base64 = await blobToRawBase64(creativeEditor.pendingBlob);
            commitCreativeBase64(creativeEditor.slot, base64, creativeEditor.file.name);
            closeCreativeEditor();
        } catch (err) {
            console.error('Preview commit failed:', err);
            alert('Could not save this creative. Please try again.');
        }
    }, [creativeEditor, closeCreativeEditor, commitCreativeBase64]);

    async function onSubmit(e) {
        e.preventDefault();
        if (!campaignName.trim()) {
            alert('Enter a campaign name.');
            return;
        }
        if (!campaignHorizontalContent || !campaignVerticalContent) {
            alert('Upload both horizontal and vertical content.');
            return;
        }

        setSubmitting(true);
        setSubmitError('');

        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/advertiser/campaign-stripe/complete-prepaid`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: token,
                    },
                    body: JSON.stringify({
                        advertisementId: adId,
                        campaignName: campaignName.trim(),
                        campaignHorizontalContent,
                        campaignVerticalContent,
                    }),
                }
            );
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data.error || 'Submission failed');
            }
            navigate('/advertiserDashboard/campaignDetails', {
                state: { adId },
                replace: true,
            });
        } catch (err) {
            setSubmitError(err.message || 'Something went wrong.');
        } finally {
            setSubmitting(false);
        }
    }

    if (loading) {
        return (
            <div className="advertiserDashboardContainerCampaigns">
                <div className="adminDashboardScreenPageContainer">
                    <p>Loading reservation…</p>
                </div>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="advertiserDashboardContainerCampaigns">
                <div className="adminDashboardScreenPageContainer">
                    <p style={{ color: '#C52F00' }}>{loadError}</p>
                    <button type="button" className="advertiserRegisterSubmitBtn" onClick={() => navigate(-1)}>
                        Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="advertiserDashboardContainerCampaigns">
            {creativeProcessing && (
                <CreativeProcessingOverlay message={creativeProcessing.message} />
            )}

            {creativeEditor?.step === 'crop' && (
                <CreativeCropModal
                    slot={creativeEditor.slot}
                    imageSrc={creativeEditor.imageSrc}
                    onCancel={handleCreativeEditorCancel}
                    onNext={handleCropNext}
                    exporting={cropExporting}
                />
            )}
            {creativeEditor?.step === 'preview' && (
                <CreativePreviewModal
                    slot={creativeEditor.slot}
                    previewUrl={creativeEditor.previewUrl}
                    isVideo={creativeEditor.isVideo}
                    onCancel={handleCreativeEditorCancel}
                    onNext={handlePreviewNext}
                />
            )}

            <div className={`adminDashboardScreenPageContainer ${creativeProcessing ? 'newCampaignFormBlocked' : ''}`}>
                <span className="registeredScreensTitle advertiserListTitle">Upload campaign creative</span>
                <p className="monthlyFeeSubtext" style={{ marginBottom: '1.5rem' }}>
                    Complete your prepaid reservation: <strong>{reservationLabel}</strong>
                </p>

                <form
                    className={creativeProcessing ? 'newCampaignFormBlocked' : ''}
                    onSubmit={onSubmit}
                >
                    <div className="newCampaignInputContainer">
                        <span className="newCampaignInputTitle">Campaign name</span>
                        <input
                            value={campaignName}
                            onChange={(e) => setCampaignName(e.target.value)}
                            required
                            type="text"
                            maxLength={50}
                            placeholder="Campaign name"
                            className="newCampaignInputInputFull"
                        />
                    </div>

                    <div className="newCampaignInputTwoContainer">
                        <span className="newCampaignInputTitle">Horizontal content (16:9 landscape)</span>
                        <span className="newCampaignInputHint">
                            Images: any aspect ratio — we will help you crop. Large images are resized automatically. {creativeVideoHint('horizontal')}
                        </span>
                        <CreativeFileUpload
                            inputId={CREATIVE_INPUT_IDS.horizontal}
                            accept="image/*,video/mp4,video/x-m4v,video/*"
                            fileName={creativeFileNames.horizontal}
                            isReady={Boolean(campaignHorizontalContent)}
                            disabled={Boolean(creativeProcessing) || submitting}
                            onChange={(e) => processCreativeUpload(e, 'horizontal')}
                        />
                    </div>

                    <div className="newCampaignInputTwoContainer">
                        <span className="newCampaignInputTitle">Vertical content (9:16 portrait)</span>
                        <span className="newCampaignInputHint">
                            Images: any aspect ratio — we will help you crop. Large images are resized automatically. {creativeVideoHint('vertical')}
                        </span>
                        <CreativeFileUpload
                            inputId={CREATIVE_INPUT_IDS.vertical}
                            accept="image/*,video/mp4,video/x-m4v,video/*"
                            fileName={creativeFileNames.vertical}
                            isReady={Boolean(campaignVerticalContent)}
                            disabled={Boolean(creativeProcessing) || submitting}
                            onChange={(e) => processCreativeUpload(e, 'vertical')}
                        />
                    </div>

                    {submitError && <p style={{ color: '#C52F00', marginBottom: '1rem' }}>{submitError}</p>}

                    <input
                        type="submit"
                        className="advertiserRegisterSubmitBtn"
                        value={submitting ? 'Submitting…' : 'Submit for approval'}
                        disabled={submitting || Boolean(creativeProcessing)}
                    />
                </form>
            </div>
        </div>
    );
}
